import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { get, over, set } from '@totalsoft/react-state-lens';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { useTranslation } from 'react-i18next';
import { valueOrDefault } from 'utils/functions';
import { DateTime } from '@ts-private/rocket-extensions';
import { useFieldDecimalPlacesConfig } from 'hooks';
import LabeledCheckbox from 'components/common/inputs/LabeledCheckbox';
import FlexBox from 'components/common/FlexBox';

const ResidualValueGridGeneralDataComponent = ({ lens, residualValues, validation, disabled, isNew }) => {
  const { t } = useTranslation();
  const { cvsChPercent } = useFieldDecimalPlacesConfig();

  const applicableTo = { id: lens.applicableToId |> get, name: lens.applicableToName |> get };

  const handleChange = prefix => value => {
    over(lens, prev => ({ ...prev, [`${prefix}Id`]: value.id, [`${prefix}Name`]: value.name }));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          label={t('ResidualValueGrid.Code')}
          fullWidth
          required
          value={lens.code |> get |> valueOrDefault('')}
          onChange={lens.code |> set}
          error={!isValid(validation.code)}
          helperText={getErrors(validation.code)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label={t('ResidualValueGrid.Name')}
          fullWidth
          required
          value={lens.name |> get |> valueOrDefault('')}
          onChange={lens.name |> set}
          error={!isValid(validation.name)}
          helperText={getErrors(validation.name)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          label={t('ResidualValueGrid.ApplicableTo')}
          value={applicableTo}
          onChange={handleChange('applicableTo')}
          options={residualValues}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateTime
          label={t('ResidualValueGrid.FromDate')}
          value={lens.fromDate |> get}
          onChange={lens.fromDate |> set}
          disabled={disabled}
          error={!isValid(validation.fromDate)}
          helperText={getErrors(validation.fromDate)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateTime
          label={t('ResidualValueGrid.ToDate')}
          value={lens.toDate |> get}
          onChange={lens.toDate |> set}
          disabled={disabled}
          error={!isValid(validation.toDate)}
          helperText={getErrors(validation.toDate)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          isNumeric
          decimalScale={cvsChPercent}
          label={t('ResidualValueGrid.MaximumIncrease')}
          value={lens.maximumIncrease |> get}
          onChange={lens.maximumIncrease |> set}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          isNumeric
          decimalScale={cvsChPercent}
          label={t('ResidualValueGrid.MaximumDecrease')}
          value={lens.maximumDecrease |> get}
          onChange={lens.maximumDecrease |> set}
          disabled={disabled}
        />
      </Grid>
      <FlexBox xs={{ direction: 'column', gap: 0 }} gap={2} pl={2} pt={2}>
        <LabeledCheckbox
          label={t('ResidualValueGrid.IsActive')}
          checked={lens.isActive |> get |> valueOrDefault(false)}
          onChange={lens.isActive |> set}
          disabled={disabled}
        />
        <LabeledCheckbox
          label={t('ResidualValueGrid.NetRiskValue')}
          checked={lens.netRiskValue |> get |> valueOrDefault(false)}
          onChange={lens.netRiskValue |> set}
          disabled={disabled}
        />
        <LabeledCheckbox
          label={t('ResidualValueGrid.AssetLevelResidualValues')}
          checked={lens.assetLevelResidualValues |> get |> valueOrDefault(false)}
          onChange={lens.assetLevelResidualValues |> set}
          disabled={disabled || !isNew}
          required
        />
      </FlexBox>
    </Grid>
  );
};

ResidualValueGridGeneralDataComponent.propTypes = {
  lens: PropTypes.object,
  residualValues: PropTypes.array,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  isNew: PropTypes.bool
};

export default ResidualValueGridGeneralDataComponent;
